import React from 'react';
import { connect } from 'react-redux';
import { __RouterContext } from 'react-router';
import { Route, Switch, matchPath } from 'react-router-dom';
import { runSaga } from 'redux-saga';

import { Center, Spinner } from '@frontend/legacy-components';

import Error from 'containers/Error/Error';

import { ErrorBoundary } from 'modules/common/components';
import { setId } from 'modules/common/helpMode/data/actions';
import { partViewerRoutePrefix } from 'modules/viewerPage/data/routes';

import * as appActions from 'store/app/actions';
import * as appSelectors from 'store/app/selectors';
import { GlobalStore } from 'store/configureStore';
import * as sessionSelectors from 'store/session/selectors';

import history from 'utils/browserHistory';

const routesThatNeedToBeUpgraded = ['/processes', partViewerRoutePrefix];

const connector = connect((state, props) => ({
  isLoaded: appSelectors.getIsLoaded(state),
  isFetching: appSelectors.getIsFetching(state),
  error: appSelectors.getError(state),
  isLoggedIn: sessionSelectors.getIsLoggedIn(state),
  locationFromStore: state.app.location,
  nextLocationFromStore: state.app.nextLocation,
  shouldUseLocationFromStore:
    state.app.location &&
    routesThatNeedToBeUpgraded.some((path) =>
      props.location?.pathname.includes(path),
    ),
}));

const CustomRoute = ({ component = () => null, saga, ...props }) => {
  return (
    <Route
      {...props}
      render={(props) => (
        <ErrorBoundary showBackButton={true}>
          <ConnectedComponentWrapper
            {...props}
            component={component}
            saga={saga}
          />
        </ErrorBoundary>
      )}
    />
  );
};

/**
 * Handle page loading error.
 * @param {Function} dispatch Redux dispatch method
 * @param {Object} error
 */
const handleError = (dispatch, error) => {
  dispatch(appActions.page.error({ error }));
  console.error(error);
};

const handleComponentPreloadError = (error) => {
  if (/Loading chunk \d+ failed/.test(error.toString())) {
    const threshold = 5000;
    const lastReload = window.localStorage.getItem('lastReload');
    const millisAfterLastReload = lastReload
      ? new Date() - new Date(lastReload)
      : threshold + 1;
    // ensure that we automatically reload the page only once in 5 seconds
    if (millisAfterLastReload > threshold) {
      window.localStorage.setItem('lastReload', new Date().toJSON());
      window.location.reload();
      // return promise which will never resolve or reject
      // to pause page loading until window.location.reload() is executed
      return new Promise(() => null);
    }
  }
  throw error;
};

class CustomSwitch extends React.Component {
  static contextType = __RouterContext;

  task = null;

  findMatchingElement = (props, context) => {
    const { match: routerContextMatch } = context;
    const { children } = props;
    const location = props.location;

    let match, child;
    React.Children.forEach(children, (element) => {
      if (match == null && React.isValidElement(element)) {
        const {
          path: pathProp,
          exact,
          strict,
          sensitive,
          from,
        } = element.props;
        const path = pathProp || from;

        child = element;
        match = matchPath(
          location.pathname,
          { path, exact, strict, sensitive },
          routerContextMatch,
        );
      }
    });

    return { match, child };
  };

  // TODO: REPLACE THIS WITH SOMETHIz
  startSaga = (saga, opts) => {
    const options = {
      dispatch: this.props.dispatch,
      getState: GlobalStore.getState,
    };
    const args = {
      ...opts,
      setIsLoaded: (value) =>
        this.props.dispatch(appActions.setIsLoaded({ isLoaded: value })),
      setIsFetching: (value) =>
        this.props.dispatch(appActions.setIsFetching({ isFetching: value })),
    };
    return runSaga(options, saga, args);
  };

  changeRoute = async (props, context) => {
    const { child, match } = this.findMatchingElement(props, context);
    const { saga, state, helpId, component, permissions = [] } = child.props;
    for (const permission of permissions) {
      if (!permission(GlobalStore.getState())) {
        history.push(
          this.props.isLoggedIn
            ? '/'
            : '/login?next=' +
                encodeURIComponent(
                  props.location.pathname + props.location.search,
                ),
        );
        return;
      }
    }

    this.props.dispatch(setId(helpId));

    this.props.dispatch(
      appActions.page.request({
        location: {
          ...props.location,
          state: props.location.state || state,
        },
      }),
    );

    try {
      // if there is a saga in progress then cancel it
      if (this.task) {
        this.task.cancel();
        // HACK: task.isCancelled() function is not working somehow...
        // so we track cancellation with __CANCELLED__ property
        this.task.__CANCELLED__ = true;
      }

      if (component && typeof component.preload === 'function') {
        try {
          await component.preload();
        } catch (error) {
          await handleComponentPreloadError(error);
        }
      }

      if (!saga) {
        this.props.dispatch(appActions.page.success({}));
      } else {
        const task = this.startSaga(saga, { location, match });
        this.task = task;
        await task.done;
        if (!task.__CANCELLED__) {
          this.props.dispatch(
            appActions.page.success({ model: task.result() }),
          );
        }
      }
    } catch (error) {
      handleError(this.props.dispatch, error);
    }
  };

  async componentDidMount() {
    this.changeRoute(this.props, this.context);
  }

  async UNSAFE_componentWillReceiveProps(nextProps, nextContext) {
    const locationChanged = nextProps.location !== this.props.location;
    if (locationChanged) {
      this.changeRoute(nextProps, nextContext);
    }
  }

  render() {
    const location = this.props.shouldUseLocationFromStore
      ? this.props.locationFromStore
      : this.props.location;

    if (this.props.isLoaded && this.props.error) {
      return <Error error={this.props.error} />;
    }

    if (!location) {
      return null;
    }

    return <Switch location={location}>{this.props.children}</Switch>;
  }
}

const ConnectedCustomSwitch = connector(CustomSwitch);
ConnectedCustomSwitch.displayName = 'ConnectedCustomSwitch';

class ComponentWrapper extends React.Component {
  render() {
    const { component, ...props } = this.props;

    if (!props.shouldUseLocationFromStore && !props.isLoaded && props.saga) {
      return (
        <Center h="100vh">
          <Spinner size="xl" label="Loading Page" />
        </Center>
      );
    }

    return React.createElement(component, {
      ...props,
      onError: (error) => {
        handleError(this.props.dispatch, error);
      },
    });
  }
}

const ConnectedComponentWrapper = connector(ComponentWrapper);

export { ConnectedCustomSwitch as CustomSwitch, CustomRoute };
